import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    prefecture: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d606oqumq2rq4g9vond0?email=muramatsu@risecorp.jp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          prefecture: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-12 md:py-24 bg-gradient-to-b from-teal-50/50 to-emerald-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-400 to-emerald-400 text-white px-5 py-2 md:px-8 md:py-3 rounded-full text-sm md:text-lg font-bold mb-4 md:mb-6 shadow-lg">
            <span className="text-lg md:text-xl">📞</span>
            まずは一度、お見積りをご依頼ください！
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-3 md:mb-4">
            無料相談・お見積り
          </h2>
          <div className="w-16 md:w-20 h-1 md:h-1.5 bg-gradient-to-r from-teal-400 to-emerald-400 mx-auto mt-4 md:mt-6 rounded-full"></div>
          <p className="text-gray-600 text-sm md:text-base mt-4 md:mt-6">
            お電話またはフォームからお気軽にお問い合わせください
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5 md:gap-8 mb-10 md:mb-16">
          {/* Contact Info */}
          <div className="space-y-4 md:space-y-6">
            <div className="bg-gradient-to-br from-teal-400 to-emerald-400 rounded-2xl md:rounded-3xl p-5 md:p-8 text-white shadow-lg">
              <h3 className="text-base md:text-xl font-bold mb-3 md:mb-4 flex items-center gap-2 md:gap-3">
                <span className="text-xl md:text-2xl">📱</span>
                お電話でのお問い合わせ
              </h3>
              <a 
                href="tel:0120-710-056"
                className="block text-2xl md:text-4xl font-bold mb-2 md:mb-3 hover:text-emerald-100 transition-colors cursor-pointer"
              >
                0120-710-056
              </a>
              <p className="text-white/90 text-xs md:text-sm flex items-center gap-2">
                <i className="ri-time-line"></i>
                受付時間：平日 9:00〜18:00
              </p>
            </div>

            <div className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-md border border-teal-100">
              <h3 className="text-base md:text-xl font-bold mb-4 md:mb-6 text-gray-800 flex items-center gap-2 md:gap-3">
                <span className="text-xl md:text-2xl">🏢</span>
                会社情報
              </h3>
              <div className="space-y-3 md:space-y-4 text-gray-600">
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-4-line text-teal-500 text-sm md:text-base"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 mb-0.5 md:mb-1 text-xs md:text-sm">会社名</p>
                    <a 
                      href="https://risecorp.jp/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs md:text-sm text-teal-600 hover:text-teal-700 hover:underline transition-colors cursor-pointer"
                    >
                      株式会社ライズコーポレーション
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-teal-500 text-sm md:text-base"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 mb-0.5 md:mb-1 text-xs md:text-sm">所在地</p>
                    <p className="text-xs md:text-sm">〒543-0002<br />大阪市天王寺区上汐3-8-24<br />ライズ上汐ビル8階</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-md border border-teal-100">
            <h3 className="text-base md:text-xl font-bold mb-4 md:mb-6 text-gray-800 flex items-center gap-2 md:gap-3">
              <span className="text-xl md:text-2xl">✉️</span>
              お問い合わせフォーム
            </h3>
            
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-3 md:space-y-4">
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  会社名 <span className="text-teal-500">*</span>
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-200 rounded-lg md:rounded-xl focus:border-teal-400 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all text-sm"
                  placeholder="例：株式会社〇〇工務店"
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  お名前 <span className="text-teal-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-200 rounded-lg md:rounded-xl focus:border-teal-400 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all text-sm"
                  placeholder="例：山田 太郎"
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  メールアドレス <span className="text-teal-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-200 rounded-lg md:rounded-xl focus:border-teal-400 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all text-sm"
                  placeholder="例：info@example.com"
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  電話番号 <span className="text-teal-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-200 rounded-lg md:rounded-xl focus:border-teal-400 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all text-sm"
                  placeholder="例：06-1234-5678"
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  都道府県 <span className="text-teal-500">*</span>
                </label>
                <select
                  name="prefecture"
                  value={formData.prefecture}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-200 rounded-lg md:rounded-xl focus:border-teal-400 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="大阪府">大阪府</option>
                  <option value="兵庫県">兵庫県</option>
                  <option value="滋賀県">滋賀県</option>
                  <option value="和歌山県">和歌山県</option>
                  <option value="奈良県">奈良県</option>
                  <option value="京都府">京都府</option>
                  <option value="三重県">三重県</option>
                  <option value="岐阜県">岐阜県</option>
                  <option value="愛知県">愛知県</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  お問い合わせ内容
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-200 rounded-lg md:rounded-xl focus:border-teal-400 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all resize-none text-sm"
                  placeholder="ご質問やご要望などをご記入ください（500文字以内）"
                />
                <p className="text-[10px] md:text-xs text-gray-400 mt-1 text-right">
                  {formData.message.length}/500文字
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-teal-400 to-emerald-400 text-white py-3 md:py-4 rounded-full font-bold text-sm md:text-base hover:from-teal-500 hover:to-emerald-500 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    送信中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill mr-2"></i>
                    送信する
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg md:rounded-xl p-3 md:p-4 text-center">
                  <span className="text-xl md:text-2xl mb-1 md:mb-2 block">✅</span>
                  <p className="text-green-700 font-medium text-sm">送信が完了しました！</p>
                  <p className="text-green-600 text-xs md:text-sm mt-1">担当者より折り返しご連絡いたします。</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg md:rounded-xl p-3 md:p-4 text-center">
                  <span className="text-xl md:text-2xl mb-1 md:mb-2 block">⚠️</span>
                  <p className="text-red-700 font-medium text-sm">送信に失敗しました</p>
                  <p className="text-red-600 text-xs md:text-sm mt-1">お手数ですが、もう一度お試しください。</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-teal-100 to-emerald-100 py-8 md:py-12 mt-12 md:mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <img 
              src="https://static.readdy.ai/image/a44b33c2a19c260b8697c4ae9afddc14/df05b769b1b7d05ef32882c327169def.png" 
              alt="ライズコーポレーション" 
              className="h-6 md:h-10 mx-auto mb-3 md:mb-4"
            />
            <p className="text-gray-700 font-medium mb-1 text-sm md:text-base">株式会社ライズコーポレーション</p>
            <p className="text-gray-500 text-xs md:text-sm mb-4 md:mb-6">
              〒543-0002 大阪市天王寺区上汐3-8-24 ライズ上汐ビル8階
            </p>
            <div className="border-t border-teal-200 pt-4 md:pt-6">
              <p className="text-gray-500 text-xs md:text-sm">
                © 2024 Rise Corporation. All rights reserved. | 
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-teal-500 transition-colors ml-1 cursor-pointer">
                  Powered by Readdy
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </section>
  );
}
